<script>
  // Экран входа (этап 16): сессия закончилась — Face ID одним касанием, сертификат
  // как запасной путь. Показывается App.svelte по событию app:login-required вместо
  // прежнего немедленного ухода на сертификатный хост.
  import { createEventDispatcher, onMount } from 'svelte';
  import { goToCertificateLogin } from '../api.js';
  import { isPasskeySupported, isPlatformAuthenticatorAvailable, isUserCancel, loginWithPasskey } from '../passkey.js';
  import { TEXT } from '../ui-text.js';
  import BuildStamp from './BuildStamp.svelte';

  const dispatch = createEventDispatcher();

  let passkeyAvailable = isPasskeySupported();
  let busy = false;
  let message = '';
  let inviteCode = '';

  // Код — из символов приглашения (без 0/O/1/I), регистр неважен: backend сравнивает
  // верхний. Вход — навигацией, как по ссылке из QR: страница ставит куку и ведёт
  // в настройки с предложением включить Face ID.
  $: normalizedCode = inviteCode.replace(/[^0-9a-z]/gi, '').toUpperCase();
  $: canSubmitCode = normalizedCode.length >= 4;

  function submitInviteCode() {
    if (!canSubmitCode) {
      return;
    }
    window.location.assign(`/api/session/join?code=${encodeURIComponent(normalizedCode)}`);
  }

  onMount(async () => {
    passkeyAvailable = await isPlatformAuthenticatorAvailable();
  });

  async function handlePasskey() {
    busy = true;
    message = '';
    try {
      const result = await loginWithPasskey();
      dispatch('login', { userId: result?.userId || null });
    } catch (error) {
      console.warn('passkey login failed', error);
      message = isUserCancel(error) ? TEXT.auth.loginCancelled : TEXT.auth.loginFailed;
    } finally {
      busy = false;
    }
  }
</script>

<div class="login-screen" role="dialog" aria-labelledby="login-title">
  <h2 id="login-title">{TEXT.auth.loginTitle}</h2>
  <p class="lead">{TEXT.auth.loginLead}</p>
  {#if passkeyAvailable}
    <button type="button" class="btn-primary" disabled={busy} on:click={handlePasskey}>
      {busy ? TEXT.auth.loginBusy : TEXT.auth.loginPasskey}
    </button>
  {:else}
    <p class="hint">{TEXT.auth.loginPasskeyUnsupported}</p>
  {/if}
  {#if message}
    <p class="hint message" role="status">{message}</p>
  {/if}
  <form class="code-form" on:submit|preventDefault={submitInviteCode}>
    <label for="invite-code">{TEXT.auth.loginCodeLabel}</label>
    <input
      id="invite-code"
      bind:value={inviteCode}
      placeholder={TEXT.auth.loginCodePlaceholder}
      autocomplete="one-time-code"
      autocapitalize="characters"
      autocorrect="off"
      spellcheck="false"
      maxlength="32"
    />
    <button type="submit" class="btn-secondary" disabled={!canSubmitCode}>{TEXT.auth.loginCodeSubmit}</button>
    <p class="hint">{TEXT.auth.loginCodeHint}</p>
  </form>
  <button type="button" class="link" on:click={goToCertificateLogin}>{TEXT.auth.loginCertificate}</button>
  <BuildStamp />
</div>

<style>
  .login-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
  }

  .lead {
    color: var(--text-secondary);
    max-width: 28rem;
  }

  .btn-primary {
    min-width: 14rem;
    padding: 0.85rem 1.5rem;
    border: none;
    border-radius: 12px;
    background: var(--accent-color);
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
  }

  .btn-primary:disabled {
    opacity: 0.6;
  }

  .link {
    background: none;
    border: none;
    color: var(--accent-color);
    font-size: 0.95rem;
    text-decoration: underline;
    padding: 0.5rem;
  }

  .hint {
    color: var(--text-secondary);
    font-size: 0.9rem;
    max-width: 28rem;
  }

  .message {
    color: var(--text-primary);
  }

  .code-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    max-width: 18rem;
  }

  .code-form label {
    color: var(--text-secondary);
    font-size: 0.9rem;
  }

  .code-form input {
    width: 100%;
    min-height: 44px;
    padding: 0.6rem 0.8rem;
    border: 1px solid var(--border-color);
    border-radius: 10px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    /* ≥16px — иначе iOS увеличивает страницу при фокусе. */
    font-size: max(16px, 1.1rem);
    letter-spacing: 0.12em;
    text-align: center;
    text-transform: uppercase;
  }

  .btn-secondary {
    min-width: 14rem;
    min-height: 44px;
    padding: 0.7rem 1.5rem;
    border: 1px solid var(--accent-color);
    border-radius: 12px;
    background: transparent;
    color: var(--accent-color);
    font-size: 1rem;
    font-weight: 600;
  }

  .btn-secondary:disabled {
    opacity: 0.5;
  }
</style>
